import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/common/SEO";
import { ROUTES } from "../../constants/routes";
import transparentLogo from "../../assets/images/Transparent_logo.png";
import { ShieldIcon } from "../../components/common/Icons";

const SECTIONS = [
  { id: "overview", num: "01", title: "Overview & Scope" },
  { id: "collection", num: "02", title: "Information We Collect" },
  { id: "gameplay-data", num: "03", title: "Gameplay & Devotion Streaks" },
  { id: "usage", num: "04", title: "How We Use Your Data" },
  { id: "third-parties", num: "05", title: "Third-Party Service Providers" },
  { id: "retention-deletion", num: "06", title: "Data Retention & Erasure Rights" },
  { id: "fair-play-privacy", num: "07", title: "Leaderboards & Public Display" },
  { id: "children", num: "08", title: "Children's Privacy (COPPA)" },
  { id: "security", num: "09", title: "Security & Encryption" },
  { id: "user-rights", num: "10", title: "Your Privacy Rights (GDPR & CCPA)" },
  { id: "contact", num: "11", title: "Contact Us & Data Officer" },
];

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState("overview");

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 120;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i].id);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(SECTIONS[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-doc-page">
      <SEO
        title="Privacy Policy — Bible Contest"
        description="Read the official Privacy Policy for Bible Contest. Learn how we collect, protect, and handle your data across our web platform and mobile applications in compliance with Google Play, Apple App Store, and GDPR policies."
        canonicalUrl="/privacy-policy"
      />

      {/* ─── Top Brand Navigation ─── */}
      <header className="privacy-top-navbar">
        <div className="privacy-nav-container">
          <Link to="/" className="privacy-nav-brand">
            <img src={transparentLogo} alt="Bible Contest Logo" className="privacy-nav-logo" />
            <span className="privacy-nav-title">Bible Contest</span>
            <span className="privacy-nav-badge">Legal Center</span>
          </Link>

          <div className="privacy-nav-links">
            <Link to="/support" className="privacy-nav-link">
              Need Help?
            </Link>
            <Link to="/" className="privacy-nav-link" style={{ color: "var(--clr-primary)" }}>
              <span>Return to App →</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ─── Hero Section ─── */}
      <section className="privacy-hero">
        <div className="privacy-hero-pill">
          <ShieldIcon size={14} />
          <span>Google Play Data Safety & Apple App Store 5.1.1(v) Compliant</span>
        </div>
        <h1 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.4rem)", fontWeight: 800, margin: "0 0 12px 0" }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: "15px", color: "var(--clr-text-2)", lineHeight: 1.6, margin: 0 }}>
          This Privacy Policy explains how Bible Contest (“We”, “Us”, or “Our”) collects, uses, protects, and discloses your personal data across our web application and mobile applications (iOS and Android).
        </p>
      </section>

      {/* ─── Quick Legal Switcher Tabs ─── */}
      <div className="legal-quick-switcher">
        <Link to={ROUTES.LEGAL.PRIVACY} className="legal-switcher-tab active">
          <span>📜</span>
          <span>Privacy Policy</span>
        </Link>
        <Link to={ROUTES.LEGAL.TERMS} className="legal-switcher-tab">
          <span>⚖️</span>
          <span>Terms & Conditions</span>
        </Link>
        <Link to={ROUTES.LEGAL.DELETION_REQUEST} className="legal-switcher-tab">
          <span>🛡️</span>
          <span>Account Erasure Portal</span>
        </Link>
      </div>

      {/* ─── Mobile Horizontal Quick Jump ─── */}
      <div className="legal-mobile-quick-jump">
        {SECTIONS.map((sec) => (
          <a key={sec.id} href={`#${sec.id}`} className="legal-mobile-pill">
            {sec.num}. {sec.title}
          </a>
        ))}
      </div>

      {/* ─── 2-Column Document Layout ─── */}
      <div className="legal-doc-layout">
        {/* Left Sticky Table of Contents */}
        <aside className="legal-sidebar">
          <div className="legal-toc-card">
            <div className="legal-toc-header">
              <h3 className="legal-toc-title">Table of Contents</h3>
              <button type="button" className="legal-print-btn" onClick={handlePrint}>
                <span>🖨️</span>
                <span>Print</span>
              </button>
            </div>
            <nav className="legal-toc-list">
              {SECTIONS.map((sec) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  className={`legal-toc-item ${activeSection === sec.id ? "active" : ""}`}
                >
                  <span className="legal-toc-num">{sec.num}</span>
                  <span>{sec.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="legal-sidebar-help">
            <div className="legal-sidebar-help-title">
              <span>🛡️</span>
              <span>Data Protection Officer</span>
            </div>
            <p style={{ margin: 0, color: "var(--clr-text-2)", lineHeight: 1.5 }}>
              Questions about how your personal data is handled? Reach our privacy desk directly at:
            </p>
            <a href="mailto:support@biblecontest.app" style={{ color: "var(--clr-primary)", fontWeight: 700, textDecoration: "none" }}>
              support@biblecontest.app
            </a>
          </div>

          <div className="legal-sidebar-help" style={{ background: "#F0FDF4", borderColor: "#BBF7D0" }}>
            <div className="legal-sidebar-help-title" style={{ color: "#15803D" }}>
              <span>✓</span>
              <span>Account Deletion Portal</span>
            </div>
            <p style={{ margin: 0, color: "#166534", lineHeight: 1.5 }}>
              Under Google Play and Apple App Store rules, you can permanently erase your account at any time.
            </p>
            <Link to={ROUTES.LEGAL.DELETION_REQUEST} style={{ color: "#15803D", fontWeight: 700, textDecoration: "underline" }}>
              Visit Erasure Portal →
            </Link>
          </div>
        </aside>

        {/* Right Main Legal Article */}
        <main className="legal-main-content">
          <article className="legal-card">
            {/* Metadata Bar */}
            <div className="legal-meta-bar">
              <div className="legal-meta-item">
                <span>Effective Date:</span>
                <strong>October 22, 2026</strong>
              </div>
              <div className="legal-meta-item">
                <span>Last Updated:</span>
                <strong>October 2026</strong>
              </div>
              <div className="legal-meta-item">
                <span>Version:</span>
                <strong>3.2 (Production Release)</strong>
              </div>
              
            </div>

            {/* Section 01 */}
            <section id="overview" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">01</span>
                <h2 className="legal-section-title">Overview & Scope</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Bible Contest (“We”, “Our”, or “Us”) is a faith-based educational and trivia ministry dedicated to building daily Scripture reading habits through joyful trivia challenges and church league tournaments.
                </p>
                <div className="legal-summary-box">
                  <div className="legal-summary-box-title">
                    <span>💡</span> In Brief
                  </div>
                  We never sell, rent, or monetize your personal information to third-party data brokers or behavioral advertising networks. We collect only what is necessary to authenticate your account, compute tournament scores and devotion streaks, and maintain fair play.
                </div>
                <p>
                  This Privacy Policy applies to all users accessing Bible Contest through:
                </p>
                <ul>
                  <li>The web application hosted at <strong>biblecontest.app</strong> and official subdomains;</li>
                  <li>The mobile applications published on the <strong>Google Play Store</strong> and <strong>Apple App Store</strong>;</li>
                  <li>Our Host Studio administration tools and church league management portals.</li>
                </ul>
              </div>
            </section>

            {/* Section 02 */}
            <section id="collection" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">02</span>
                <h2 className="legal-section-title">Information We Collect</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Depending on how you interact with our platform, we collect the following categories of information:
                </p>

                <div className="legal-table-wrap">
                  <table className="legal-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Specific Data Points Collected</th>
                        <th>Collection Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Identity & Authentication</strong></td>
                        <td>Name, email address, profile picture (URL), and unique Firebase User ID (`uid`).</td>
                        <td>Account sign-in via Google Authentication or email, session preservation, and identity verification.</td>
                      </tr>
                      <tr>
                        <td><strong>Demographic & Profile</strong></td>
                        <td>Country of residence, phone number (optional), and preferred timezone.</td>
                        <td>Calculating local tournament start times, devotion streaks, and country standings.</td>
                      </tr>
                      <tr>
                        <td><strong>Gameplay & Achievements</strong></td>
                        <td>Contest scores, questions attempted, correct answers, time taken per question, devotion streaks, and 1st-place trophies.</td>
                        <td>Displaying leaderboards, tracking personal Bible study habits, and awarding contest recognitions.</td>
                      </tr>
                      <tr>
                        <td><strong>Referral & Invitation</strong></td>
                        <td>Personal referral code, inviter's referral code, count of invited friends, and milestone achievements.</td>
                        <td>Attribution of community invitations and reward distribution.</td>
                      </tr>
                      <tr>
                        <td><strong>Technical & Diagnostics</strong></td>
                        <td>Device model, operating system version, app version, network state, crash stack traces, and anonymous performance metrics.</td>
                        <td>Application stability, crash diagnostics via Firebase Crashlytics, and anti-tamper security.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p>
                  <strong>Private vs. Public Information:</strong> Your phone number and email address are strictly private and are <em>never</em> shared, sold, or displayed on public leaderboards or contestant profiles. Public contestant profiles display only your chosen Display Name, avatar, country, timezone, and gameplay achievements.
                </p>
              </div>
            </section>

            {/* Section 03 */}
            <section id="gameplay-data" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">03</span>
                <h2 className="legal-section-title">Gameplay & Devotion Streaks</h2>
              </div>
              <div className="legal-prose">
                <p>
                  To encourage consistent daily engagement with Scripture, our platform calculates:
                </p>
                <ul>
                  <li>
                    <strong>Daily Devotion Streaks:</strong> A counter tracking consecutive days you participate in a quiz. Streaks are calculated using your configured local timezone to ensure fairness across global timezones.
                  </li>
                  <li>
                    <strong>Time in the Word:</strong> Accumulated duration spent reading and responding to Scripture trivia questions.
                  </li>
                  <li>
                    <strong>Accuracy Percentage:</strong> Cumulative percentage of correctly answered Scripture trivia questions across all official rounds.
                  </li>
                </ul>
                <p>
                  This data is stored within Google Cloud Firestore and linked to your User ID to allow seamless multi-device synchronization across iOS, Android, and web browsers.
                </p>
              </div>
            </section>

            {/* Section 04 */}
            <section id="usage" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">04</span>
                <h2 className="legal-section-title">How We Use Your Data</h2>
              </div>
              <div className="legal-prose">
                <p>
                  We process personal data on the lawful basis of contractual necessity (providing the trivia services you request) and our legitimate interest in maintaining fair play:
                </p>
                <ul>
                  <li><strong>Contest Administration:</strong> Facilitating live multiplayer quizzes, verifying answers, computing points, and determining tournament winners;</li>
                  <li><strong>Fair Play & Anti-Cheat:</strong> Monitoring answer submission intervals and anomalous completion times to prevent automated bot scripts or unfair manipulation;</li>
                  <li><strong>Customer Support:</strong> Responding to inquiries, resolving scoring discrepancies, and processing account requests;</li>
                  <li><strong>Push & Email Notifications:</strong> Sending contest alerts, season reminders, and streak expiration warnings (you can opt out at any time in device settings);</li>
                  <li><strong>Platform Optimization:</strong> Resolving application crashes and optimizing network latency for live question countdowns.</li>
                </ul>
              </div>
            </section>

            {/* Section 05 */}
            <section id="third-parties" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">05</span>
                <h2 className="legal-section-title">Third-Party Service Providers</h2>
              </div>
              <div className="legal-prose">
                <p>
                  We utilize enterprise cloud infrastructure managed by Google LLC (Firebase) to host our backend services. All third-party sub-processors are bound by strict Data Processing Agreements (DPAs) requiring adherence to international privacy standards:
                </p>
                <ul>
                  <li><strong>Google Firebase Authentication:</strong> Manages secure OAuth sign-in and encrypted credential verification.</li>
                  <li><strong>Google Cloud Firestore:</strong> Fully encrypted NoSQL cloud database storing user profiles, quizzes, questions, and contest results.</li>
                  <li><strong>Firebase Cloud Storage:</strong> Stores user-uploaded profile avatars and season banner images with restricted access policies.</li>
                  <li><strong>Firebase Cloud Messaging (FCM):</strong> Delivers live contest countdown notifications and daily study reminders to mobile devices.</li>
                  <li><strong>Firebase Crashlytics:</strong> Collects anonymized crash diagnostics and hardware stack traces to pinpoint app errors.</li>
                </ul>
                <p>
                  We do <strong>not</strong> include any third-party behavioral advertising SDKs (such as Facebook Pixel, TikTok SDK, or ad networks) within our mobile or web applications.
                </p>
              </div>
            </section>

            {/* Section 06 */}
            <section id="retention-deletion" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">06</span>
                <h2 className="legal-section-title">Data Retention & Erasure Rights</h2>
              </div>
              <div className="legal-prose">
                <p>
                  We retain personal data only for as long as your account remains active or as needed to maintain historical tournament results.
                </p>

                {/* Callout box to Erasure Portal */}
                <div className="legal-callout-action">
                  <div className="legal-callout-action-left">
                    <div className="legal-callout-action-icon">🛡️</div>
                    <div>
                      <div className="legal-callout-action-title">Account & Personal Data Erasure Portal</div>
                      <div className="legal-callout-action-sub">
                        Self-service erasure compliant with Google Play Data Safety & Apple App Store 5.1.1(v).
                      </div>
                    </div>
                  </div>
                  <Link to={ROUTES.LEGAL.DELETION_REQUEST} className="legal-callout-btn">
                    Open Erasure Portal →
                  </Link>
                </div>

                <p>
                  <strong>How the Erasure Process Operates:</strong>
                </p>
                <ol>
                  <li>
                    <strong>Submission & Verification:</strong> You sign in to our Erasure Portal with your authenticated account credentials to prove ownership.
                  </li>
                  <li>
                    <strong>7-Day Safety Grace Window:</strong> Your request enters an immediate 7-day cancellation grace period. If you change your mind or submitted accidentally, you can cancel with one click.
                  </li>
                  <li>
                    <strong>Permanent Erasure:</strong> Following the grace period, our system irreversibly purges your profile, contact details, authentication credentials, and removes your personal identifiers from historical contest records within 15 business days.
                  </li>
                </ol>
              </div>
            </section>

            {/* Section 07 */}
            <section id="fair-play-privacy" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">07</span>
                <h2 className="legal-section-title">Leaderboards & Public Display</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Bible Contest includes community leaderboards for global tournaments, seasonal church leagues, and daily streaks. The information displayed publicly is limited strictly to:
                </p>
                <ul>
                  <li>Your chosen <strong>Display Name</strong>;</li>
                  <li>Your <strong>Profile Avatar</strong> (or initials);</li>
                  <li>Your <strong>Country</strong> and <strong>Timezone</strong> city;</li>
                  <li>Your <strong>Total Points</strong>, <strong>Daily Devotion Streak</strong>, and <strong>Trophies Won</strong>.</li>
                </ul>
                <p>
                  Your email address and phone number are strictly confidential and will <strong>never</strong> be displayed on any leaderboard, search result, or public profile URL.
                </p>
              </div>
            </section>

            {/* Section 08 */}
            <section id="children" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">08</span>
                <h2 className="legal-section-title">Children's Privacy (COPPA & Family Friendly Policy)</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Bible Contest is a wholesome, family-friendly Scripture engagement platform suitable for participants of all ages, including youth groups and Sunday school students.
                </p>
                <p>
                  In accordance with the <strong>Children's Online Privacy Protection Act (COPPA)</strong> and global child privacy regulations:
                </p>
                <ul>
                  <li>We do not knowingly collect personal contact information from children under the age of 13 without verifiable parental or guardian consent;</li>
                  <li>We do not display targeted or behavioral advertisements to any user, regardless of age;</li>
                  <li>Parents or guardians who believe their child has registered an account without consent may submit an erasure request through our <Link to={ROUTES.LEGAL.DELETION_REQUEST}>Erasure Portal</Link> or email us at <strong>support@biblecontest.app</strong> for immediate deletion.</li>
                </ul>
              </div>
            </section>

            {/* Section 09 */}
            <section id="security" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">09</span>
                <h2 className="legal-section-title">Security & Encryption</h2>
              </div>
              <div className="legal-prose">
                <p>
                  We implement robust technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction:
                </p>
                <ul>
                  <li><strong>Encryption in Transit:</strong> All web and mobile communications are encrypted using Transport Layer Security (TLS 1.3 / HTTPS);</li>
                  <li><strong>Encryption at Rest:</strong> All user records, trivia questions, and result documents stored in Google Cloud Firestore are encrypted with AES-256;</li>
                  <li><strong>Granular Access Controls:</strong> Firestore Security Rules enforce strict access validation, guaranteeing that private user fields (phone number, email) can only be read by the authenticated account owner;</li>
                  <li><strong>Regular Vulnerability Audits:</strong> We periodically audit our security rules, API endpoints, and dependency packages for security vulnerabilities.</li>
                </ul>
              </div>
            </section>

            {/* Section 10 */}
            <section id="user-rights" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">10</span>
                <h2 className="legal-section-title">Your Privacy Rights (GDPR & CCPA)</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Depending on your geographic location, you hold specific statutory privacy rights under regulations such as the European General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA):
                </p>
                <ul>
                  <li><strong>Right to Access:</strong> You can view all information associated with your account on your <Link to={ROUTES.USER.PROFILE}>Profile Dashboard</Link>;</li>
                  <li><strong>Right to Rectification:</strong> You can edit and update your display name, country, phone number, and timezone at any time via the Edit Profile screen;</li>
                  <li><strong>Right to Erasure (“Right to be Forgotten”):</strong> You can initiate permanent account deletion via our <Link to={ROUTES.LEGAL.DELETION_REQUEST}>Erasure Portal</Link>;</li>
                  <li><strong>Right to Object & Restrict:</strong> You can disable push notifications or disconnect authentication providers at any time;</li>
                  <li><strong>No Selling of Personal Data:</strong> We do not sell your personal data to any third party for financial or commercial gain.</li>
                </ul>
              </div>
            </section>

            {/* Section 11 */}
            <section id="contact" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">11</span>
                <h2 className="legal-section-title">Contact Us & Data Officer</h2>
              </div>
              <div className="legal-prose">
                <p>
                  If you have questions, feedback, or legal inquiries regarding this Privacy Policy or our data handling practices, please contact our team:
                </p>
                <div className="legal-summary-box" style={{ background: "#F8FAFC", borderLeftColor: "#64748B" }}>
                  <p style={{ margin: "0 0 6px 0" }}>
                    <strong>Bible Contest Ministry — Data Protection & Privacy Desk</strong>
                  </p>
                  <p style={{ margin: "0 0 4px 0" }}>
                    Email: <a href="mailto:support@biblecontest.app" style={{ color: "var(--clr-primary)", fontWeight: 700 }}>support@biblecontest.app</a>
                  </p>
                  
                  <p style={{ margin: 0 }}>
                    Official Web Portal: <a href="https://biblecontest.app" target="_blank" rel="noopener noreferrer">https://biblecontest.app</a>
                  </p>
                </div>
                <p style={{ marginTop: "16px" }}>
                  We review and update this Privacy Policy periodically to reflect technological enhancements and regulatory updates. Any material modifications will be announced via in-app banners or notification prior to taking effect.
                </p>
              </div>
            </section>
          </article>
        </main>
      </div>

      {/* ─── Portal Footer ─── */}
      <footer className="privacy-portal-footer">
        <div className="privacy-footer-inner">
          <div>
            © {new Date().getFullYear()} Bible Contest Ministry. Dedicated to the glory of God and the study of His Word.
          </div>
          <div className="privacy-footer-links">
            <Link to={ROUTES.LEGAL.PRIVACY}>Privacy Policy</Link>
            <Link to={ROUTES.LEGAL.TERMS}>Terms of Service</Link>
            <Link to={ROUTES.LEGAL.DELETION_REQUEST}>Account Erasure Portal</Link>
            <Link to={ROUTES.PUBLIC.SUPPORT}>Support Center</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
